import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { formatDate } from '../../../config/appConfig';
import { usePmsWorkOrders, type PmsWorkOrder } from '../../pms/pms-work-orders/useData';
import GenericListWidget from './GenericListWidget';

function vesselLabel(row: PmsWorkOrder): string {
  const v = row.vessel as { vesselName?: string; name?: string } | null | undefined;
  return v?.vesselName ?? v?.name ?? (row.vesselId != null ? `#${row.vesselId}` : '—');
}

function woLabel(row: PmsWorkOrder): string {
  const number = row.workOrderNumber != null ? String(row.workOrderNumber) : '';
  const title = row.title != null ? String(row.title) : '';
  if (number && title) return `${number} · ${title}`;
  if (number) return number;
  if (title) return title;
  return `WO #${row.id}`;
}

const PmsRecentWorkOrders: React.FC<{
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
}> = ({ limit = 6, embedded, settings }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const effectiveLimit = typeof settings?.limit === 'number' ? settings.limit : limit;
  const { data, isLoading } = usePmsWorkOrders(undefined, 1, 50, {
    sortKey: 'updatedAt',
    sortDirection: 'desc',
  });

  const items = useMemo(() => {
    const rows = [...(data ?? [])];
    rows.sort((a, b) => {
      const aAt = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const bAt = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return bAt - aAt;
    });
    return rows.slice(0, effectiveLimit);
  }, [data, effectiveLimit]);

  return (
    <GenericListWidget<PmsWorkOrder>
      title="PMS · Recent work orders"
      viewAllPath="/pms/pms-work-orders"
      data={items}
      isLoading={isLoading}
      limit={effectiveLimit}
      emptyMessage="No work orders"
      getItemKey={(row) => row.id}
      embedded={embedded}
      renderItem={(row) => (
        <div
          onClick={() => navigate(`/pms/pms-work-orders/${row.id}`)}
          className="flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/40 cursor-pointer"
        >
          <div className="min-w-0">
            <p className={`text-sm font-medium truncate ${colors.text}`}>{woLabel(row)}</p>
            <p className={`text-xs ${colors.textMuted}`}>{vesselLabel(row)}</p>
          </div>
          <span className={`text-xs whitespace-nowrap ${colors.textMuted}`}>
            {formatDate(
              row.updatedAt ??
                (typeof row.plannedDueDate === 'string' ? row.plannedDueDate : undefined) ??
                row.createdAt,
            )}
          </span>
        </div>
      )}
    />
  );
};

export default PmsRecentWorkOrders;
